/**
 * ===========================================================================================
 *  THE SEAM.
 *
 *  THIS FILE IS THE ONE CHANGE that turns the cabinet from fixtures to the real API. Nothing
 *  else in web/authors/src/routes/** or features/cabinet/** knows which implementation it is
 *  talking to, because everything goes through `CabinetApi` and gets it from React context.
 *
 *  There are exactly two implementations and they are interchangeable by construction:
 *    ./fixture-cabinet-api.ts   in-memory, states-first, what the default build runs against
 *    ./http-cabinet-api.ts      one `fetch`, bearer token, cross-origin to moonx.art/authors-api
 *
 *  WHY IT DEFAULTS TO FIXTURES. Pointing at the network by default would mean every screen in a
 *  build with no API behind it renders an error on every load - and the default build is the one
 *  the accessibility leg checks. `VITE_AUTHORS_API_LIVE=true` flips it, per build, not per screen.
 *  The flag is read in ONE place for the whole app, `isLiveApiConfigured()` in src/config.ts, and
 *  re-exported below so the editor's and the calendar's seams keep reading it through this one.
 *
 *  THE TOKEN COMES FROM THE ONE TOKEN SOURCE. `getAccessToken` defaults to the auth client's
 *  current session (`features/auth/session/access-token.ts`). This seam used to THROW when the
 *  flag was set and nobody passed a token source - and nobody could, because the router mounts
 *  these screens with no props - so the live build was a stack trace at the first screen.
 * ===========================================================================================
 */
import { accessToken } from '../../auth/session/access-token';
import { setDefaultCabinetApi } from './cabinet-api';
import { createFixtureCabinetApi } from './fixture-cabinet-api';
import { createHttpCabinetApi } from './http-cabinet-api';
import { isLiveApiConfigured } from '../../../config';
import type { AccessTokenSource } from './http-cabinet-api';
import type { FixtureScenario } from './fixture-cabinet-api';
import type { CabinetApi } from './cabinet-api';

export { isLiveApiConfigured };

export interface CabinetApiSourceOptions {
  /**
   * How the bearer token is obtained. Omitted, it is the app's one token source - the auth
   * client's current session. A test passes its own; the app passes nothing.
   */
  readonly getAccessToken?: AccessTokenSource;
  /** Force one or the other, for tests and for a screenshot of a state nobody can otherwise reach. */
  readonly force?: 'fixtures' | 'http';
  readonly scenario?: FixtureScenario;
}

export function createCabinetApi(options: CabinetApiSourceOptions = {}): CabinetApi {
  const useHttp =
    options.force === 'http' || (options.force !== 'fixtures' && isLiveApiConfigured());

  if (!useHttp) {
    return createFixtureCabinetApi(
      options.scenario === undefined ? {} : { scenario: options.scenario },
    );
  }

  // BEARER-ONLY WITH NO COOKIES across origins (adr/0002), so the token source is not optional on
  // this branch - it is defaulted, never skipped. A signed-out caller gets `null` from it and the
  // request goes out bare, which the API answers 401 and the session gate turns into /sign-in.
  return createHttpCabinetApi({ getAccessToken: options.getAccessToken ?? accessToken });
}

// ===========================================================================================
// INSTALLED AS THE DEFAULT, NOT PROVIDED - the same move ../calendar/api/calendar-api-source.ts
// makes and for the same reason: the cabinet's router mounts every screen with no props and no
// provider, so a screen that REQUIRED one is a white page the moment it joins CABINET_ROUTES.
// Side-effecting on purpose; ./index.ts re-exports this module, so importing the barrel installs
// it.
//
// ★ ONE INSTANCE, BUILT ON FIRST USE, where it used to be a new client per `useCabinetApi()` call.
// `useCabinetApi()` runs on every render, so the fixture's own state - an archive, a reply - lived
// only until the next render and a refetch read a fresh copy that had never seen it. The calendar's
// seam already keeps one instance for the whole app
// (`../../calendar/api/calendar-api-source.ts`); the HTTP client holds no state, so for it
// the change is only fewer allocations. Lazy, so a module load never builds a client early.
// ===========================================================================================
let instance: CabinetApi | undefined;

setDefaultCabinetApi((): CabinetApi => {
  instance ??= createCabinetApi();
  return instance;
});
